import React, { useState } from 'react';
import { Camera, Plus, Trash2, Check, ChevronRight, ChevronLeft, Save, Calendar } from 'lucide-react';
import { MarkupType } from '../types';
import { CONTRACTORS, PP_VENDORS } from '../data/masterData';
import { PAPER_OPTIONS, COVER_OPTIONS, VENDOR_NAMES } from '../data/albumData';
import { calculateAlbumCost, calculateMarkup, formatCurrency, SERVICE_SHORT } from '../utils/helpers';

interface Props { onSave: (q: any) => void; }

const DESIGN_CHARGES = { Standard: 500, Premium: 1000, Elite: 1500 };

const CONTRACTOR_NAMES = CONTRACTORS
  .filter(c => c.status === 'Active' && c.contractor !== 'Monk Films')
  .map(c => c.contractor)
  .filter((v, i, a) => a.indexOf(v) === i);

const PP_VENDOR_NAMES = PP_VENDORS
  .filter(v => v.status === 'Active')
  .map(v => v.vendor)
  .filter((v, i, a) => a.indexOf(v) === i);

function generateGPCode(): string {
  const now = new Date();
  const yy = String(now.getFullYear()).slice(-2);
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  const rand = Math.floor(Math.random() * 9000) + 1000;
  return `GP-${yy}${mm}${dd}-${rand}`;
}

// Get unique deliverables for a contractor (all services combined, deduplicated).
// Each deliverable can have different agreed rates per service (wedding, birthday,
// etc.) — rateOptions groups those into clickable presets so Shivam can pick the
// real DB rate for the relevant service instead of retyping it, while still being
// free to type a custom agreed rate in the box.
function getContractorDeliverables(contractorName: string): { deliverable: string; rateOptions: { label: string; rate: number }[] }[] {
  const entries = CONTRACTORS.filter(c => c.contractor === contractorName && c.status === 'Active');
  const seen = new Set<string>();
  const result: { deliverable: string; rateOptions: { label: string; rate: number }[] }[] = [];
  entries.forEach(e => {
    if (!seen.has(e.deliverable)) {
      seen.add(e.deliverable);
      const byRate: Record<number, string[]> = {};
      Object.entries(e.rates).forEach(([svc, rate]) => {
        if (rate === undefined || rate === null) return;
        const r = rate as number;
        if (!byRate[r]) byRate[r] = [];
        byRate[r].push(SERVICE_SHORT[svc] || svc);
      });
      const rateOptions = Object.entries(byRate)
        .map(([rate, services]) => ({ label: services.join('/'), rate: Number(rate) }))
        .sort((a, b) => a.rate - b.rate);
      result.push({ deliverable: e.deliverable, rateOptions });
    }
  });
  return result;
}

// Get unique deliverables for a PP vendor (same rateOptions pattern as contractors)
function getPPDeliverables(vendorName: string): { deliverable: string; rateOptions: { label: string; rate: number }[] }[] {
  const entries = PP_VENDORS.filter(v => v.vendor === vendorName && v.status === 'Active');
  const seen = new Set<string>();
  const result: { deliverable: string; rateOptions: { label: string; rate: number }[] }[] = [];
  entries.forEach(e => {
    if (!seen.has(e.deliverable)) {
      seen.add(e.deliverable);
      const byRate: Record<number, string[]> = {};
      Object.entries(e.rates).forEach(([svc, rate]) => {
        if (rate === undefined || rate === null) return;
        const r = rate as number;
        if (!byRate[r]) byRate[r] = [];
        byRate[r].push(SERVICE_SHORT[svc] || svc);
      });
      const rateOptions = Object.entries(byRate)
        .map(([rate, services]) => ({ label: services.join('/'), rate: Number(rate) }))
        .sort((a, b) => a.rate - b.rate);
      result.push({ deliverable: e.deliverable, rateOptions });
    }
  });
  return result;
}

export default function GeneralPlanner({ onSave }: Props) {
  const [step, setStep] = useState(1);
  const [saved, setSaved] = useState(false);

  // Step 1 — Customer + Dates
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [eventName, setEventName] = useState('');
  const [notes, setNotes] = useState('');
  const [eventDates, setEventDates] = useState<{ date: string; label: string }[]>([{ date: '', label: 'Day 1' }]);

  // Step 2 — Contractors
  const [contractors, setContractors] = useState<{ id: string; name: string; deliverable: string; rate: number }[]>([]);
  const [draftCName, setDraftCName] = useState('');
  const [draftCSelected, setDraftCSelected] = useState<{ deliverable: string; rate: number | '' }[]>([]);
  const [showCForm, setShowCForm] = useState(false);
  // Manual "Other" contractor entry (not in DB)
  const [customCName, setCustomCName] = useState('');
  const [customCRows, setCustomCRows] = useState<{ deliverable: string; rate: number | '' }[]>([{ deliverable: '', rate: '' }]);

  // Step 3 — PP Vendors
  const [ppItems, setPPItems] = useState<{ id: string; name: string; deliverable: string; rate: number; qty: number }[]>([]);
  const [draftPPName, setDraftPPName] = useState('');
  const [draftPPSelected, setDraftPPSelected] = useState<{ deliverable: string; rate: number | ''; qty: number }[]>([]);
  const [showPPForm, setShowPPForm] = useState(false);
  // Manual "Other" PP vendor entry (not in DB)
  const [customPPName, setCustomPPName] = useState('');
  const [customPPRows, setCustomPPRows] = useState<{ deliverable: string; rate: number | ''; qty: number }[]>([{ deliverable: '', rate: '', qty: 1 }]);

  // Step 3 — PP opt-out
  const [includePP, setIncludePP] = useState(true); // default visible, can opt out

  // Step 4 — Album
  const [includeAlbum, setIncludeAlbum] = useState(false);
  const [vendorName, setVendorName] = useState('');
  const [paperCode, setPaperCode] = useState('');
  const [coverCode, setCoverCode] = useState('');
  const [sheets, setSheets] = useState(20);
  const [numAlbums, setNumAlbums] = useState(1);
  const [designTier, setDesignTier] = useState<'Standard' | 'Premium' | 'Elite'>('Standard');

  // Step 5 — Markup
  const [markupType, setMarkupType] = useState<MarkupType>(45);

  const numDays = Math.max(1, eventDates.filter(d => d.date).length);
  const vendorList = Object.values(VENDOR_NAMES).filter((v, i, a) => a.indexOf(v) === i);
  const papers = PAPER_OPTIONS.filter(p => p.vendorName === vendorName);
  const covers = COVER_OPTIONS.filter(c => c.vendorName === vendorName);
  const sizes = papers.map(p => p.size).filter((v, i, a) => a.indexOf(v) === i);
  const selPaper = PAPER_OPTIONS.find(p => p.paperCode === paperCode);
  const selCover = COVER_OPTIONS.find(c => c.coverCode === coverCode);
  const designCharge = DESIGN_CHARGES[designTier];
  const albumCost = includeAlbum && selPaper && selCover
    ? calculateAlbumCost({ paperRatePerSheet: selPaper.ratePerSheet, coverRate: selCover.rate, sheets, numAlbums, designChargeAmount: designCharge })
    : 0;

  const contractorTotal = contractors.reduce((s, c) => s + c.rate, 0);
  const ppTotal = ppItems.reduce((s, p) => s + (p.rate * (p.qty||1)), 0);
  const totalCost = (contractorTotal * numDays) + ppTotal + albumCost;
  const { markupAmount, quotedAmount, markupPercent } = calculateMarkup(totalCost, markupType);

  // When contractor changes, load their deliverables with empty rates
  const handleContractorChange = (name: string) => {
    setDraftCName(name);
    if (name === '__other__') {
      setDraftCSelected([]);
      setCustomCName(''); setCustomCRows([{ deliverable: '', rate: '' }]);
      return;
    }
    const delivs = getContractorDeliverables(name);
    setDraftCSelected(delivs.map(d => ({ deliverable: d.deliverable, rate: '' })));
  };

  const handlePPChange = (name: string) => {
    setDraftPPName(name);
    if (name === '__other__') {
      setDraftPPSelected([]);
      setCustomPPName(''); setCustomPPRows([{ deliverable: '', rate: '', qty: 1 }]);
      return;
    }
    const delivs = getPPDeliverables(name);
    setDraftPPSelected(delivs.map(d => ({ deliverable: d.deliverable, rate: '', qty: 1 })));
  };

  const addContractors = () => {
    if (draftCName === '__other__') {
      const valid = customCRows.filter(r => r.deliverable.trim() && r.rate !== '' && Number(r.rate) > 0);
      if (!customCName.trim() || valid.length === 0) return;
      const newItems = valid.map(r => ({
        id: `c_${Date.now()}_${Math.random()}`,
        name: customCName.trim(),
        deliverable: r.deliverable.trim(),
        rate: Number(r.rate),
      }));
      setContractors(prev => [...prev, ...newItems]);
      setDraftCName(''); setCustomCName(''); setCustomCRows([{ deliverable: '', rate: '' }]); setShowCForm(false);
      return;
    }
    const valid = draftCSelected.filter(d => d.rate !== '' && Number(d.rate) > 0);
    if (!draftCName || valid.length === 0) return;
    const newItems = valid.map(d => ({
      id: `c_${Date.now()}_${Math.random()}`,
      name: draftCName,
      deliverable: d.deliverable,
      rate: Number(d.rate),
    }));
    setContractors(prev => [...prev, ...newItems]);
    setDraftCName(''); setDraftCSelected([]); setShowCForm(false);
  };

  const addPPItems = () => {
    if (draftPPName === '__other__') {
      const valid = customPPRows.filter(r => r.deliverable.trim() && r.rate !== '' && Number(r.rate) > 0);
      if (!customPPName.trim() || valid.length === 0) return;
      const newItems = valid.map(r => ({
        id: `pp_${Date.now()}_${Math.random()}`,
        name: customPPName.trim(),
        deliverable: r.deliverable.trim(),
        rate: Number(r.rate),
        qty: r.qty || 1,
      }));
      setPPItems(prev => [...prev, ...newItems]);
      setDraftPPName(''); setCustomPPName(''); setCustomPPRows([{ deliverable: '', rate: '', qty: 1 }]); setShowPPForm(false);
      return;
    }
    const valid = draftPPSelected.filter(d => d.rate !== '' && Number(d.rate) > 0);
    if (!draftPPName || valid.length === 0) return;
    const newItems = valid.map(d => ({
      id: `pp_${Date.now()}_${Math.random()}`,
      name: draftPPName,
      deliverable: d.deliverable,
      rate: Number(d.rate),
      qty: d.qty || 1,
    }));
    setPPItems(prev => [...prev, ...newItems]);
    setDraftPPName(''); setDraftPPSelected([]); setShowPPForm(false);
  };

  const handleSave = () => {
    const q: any = {
      id: `gp_${Date.now()}`,
      quoteNumber: generateGPCode(),
      type: 'general',
      customerName, customerPhone, eventName, notes,
      eventDates, numDays,
      contractors, ppVendors: ppItems,
      album: includeAlbum && selPaper && selCover ? {
        vendorName: selPaper.vendorName, paperType: selPaper.paperType,
        paperCode, paperRatePerSheet: selPaper.ratePerSheet,
        coverType: selCover.coverType, coverCode, coverRate: selCover.rate,
        sheets, numAlbums, designChargeAmount: designCharge, totalAlbumCost: albumCost,
      } : undefined,
      markupType, totalCost, markupAmount, quotedAmount,
      createdAt: new Date().toISOString(), status: 'draft',
    };
    onSave(q);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  if (saved) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 400, gap: 16 }}>
      <div style={{ width: 60, height: 60, borderRadius: '50%', background: 'var(--teal)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Check size={26} color="white" />
      </div>
      <h2>General Quote Saved!</h2>
      <p style={{ color: 'var(--text-secondary)' }}>{formatCurrency(quotedAmount)} quoted to {customerName}</p>
    </div>
  );

  const STEPS = ['Customer & Dates', 'Contractor', 'Post Prod.', 'Album', 'Summary'];

  return (
    <div style={{ padding: 28, maxWidth: 800 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <Camera size={20} color="var(--gold)" />
        <div>
          <h1>General Purpose Planner</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: 2 }}>Any shoot — pick from contractor/vendor database, enter agreed rates</p>
        </div>
      </div>

      {/* Step indicator */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 28 }}>
        {STEPS.map((s, i) => (
          <React.Fragment key={i}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5 }}>
              <div className={`step-dot ${i + 1 === step ? 'step-dot-active' : i + 1 < step ? 'step-dot-done' : 'step-dot-inactive'}`}>
                {i + 1 < step ? <Check size={12} /> : i + 1}
              </div>
              <span style={{ fontSize: 11, fontWeight: i + 1 === step ? 600 : 400, color: i + 1 === step ? 'var(--gold)' : i + 1 < step ? 'var(--teal)' : 'var(--text-muted)', whiteSpace: 'nowrap' }}>{s}</span>
            </div>
            {i < STEPS.length - 1 && <div style={{ flex: 1, height: 1, background: i + 1 < step ? 'var(--teal)' : 'var(--border)', margin: '0 8px', marginBottom: 20, minWidth: 14 }} />}
          </React.Fragment>
        ))}
      </div>

      {/* ── STEP 1: Customer + Dates ── */}
      {step === 1 && (
        <div className="card">
          <h3 style={{ marginBottom: 16 }}>Customer & Event Details</h3>
          <div className="form-row" style={{ marginBottom: 12 }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Customer Name *</label>
              <input className="input" placeholder="Full name" value={customerName} onChange={e => setCustomerName(e.target.value)} />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Phone</label>
              <input className="input" placeholder="Phone number" value={customerPhone} onChange={e => setCustomerPhone(e.target.value)} />
            </div>
          </div>
          <div className="form-group">
            <label>Event Name *</label>
            <input className="input" placeholder="e.g. Corporate Shoot, Product Shoot, Birthday" value={eventName} onChange={e => setEventName(e.target.value)} />
          </div>

          {/* Event dates */}
          <div style={{ marginBottom: 14 }}>
            <label>Event Date(s)</label>
            {eventDates.map((ed, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                <input type="date" className="input" value={ed.date}
                  onChange={e => { const d = [...eventDates]; d[i] = { ...d[i], date: e.target.value }; setEventDates(d); }} />
                <input className="input" placeholder={`Day ${i + 1} label`} value={ed.label}
                  onChange={e => { const d = [...eventDates]; d[i] = { ...d[i], label: e.target.value }; setEventDates(d); }} />
                {eventDates.length > 1 && (
                  <button className="btn btn-ghost btn-sm btn-icon" onClick={() => setEventDates(prev => prev.filter((_, j) => j !== i))}>
                    <Trash2 size={12} color="var(--red)" />
                  </button>
                )}
              </div>
            ))}
            <button onClick={() => setEventDates(prev => [...prev, { date: '', label: `Day ${prev.length + 1}` }])}
              style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'transparent', border: '1px dashed var(--border)', borderRadius: 6, padding: '6px 12px', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 12, fontFamily: 'inherit' }}>
              <Plus size={12} /> Add another day
            </button>
          </div>

          {eventDates.filter(d => d.date).length > 1 && (
            <div style={{ marginBottom: 14, padding: '8px 12px', background: 'var(--teal-bg)', border: '1px solid #9AE6B4', borderRadius: 6, fontSize: 13, color: 'var(--teal)' }}>
              📅 {eventDates.filter(d => d.date).length} days — contractor rates will be multiplied by number of days
            </div>
          )}

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>Notes (optional)</label>
            <textarea className="textarea" placeholder="Any notes about this shoot..." value={notes} onChange={e => setNotes(e.target.value)} style={{ minHeight: 60 }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
            <button className="btn btn-primary" onClick={() => setStep(2)} disabled={!customerName.trim() || !eventName.trim()}>
              Continue <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}

      {/* ── STEP 2: Contractors ── */}
      {step === 2 && (
        <div>
          <h3 className="gold-accent" style={{ marginBottom: 4 }}>Contractors</h3>
          <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 16 }}>
            Pick contractor → their deliverables load from DB → enter agreed rate for each. Rate × {numDays} day(s).
          </p>

          {/* Added contractors */}
          {contractors.length > 0 && (
            <div className="card" style={{ marginBottom: 14 }}>
              {contractors.map((c, i) => (
                <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderBottom: i < contractors.length - 1 ? '1px solid var(--border)' : 'none' }}>
                  <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--teal-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: 'var(--teal)', flexShrink: 0 }}>
                    {c.name[0]}
                  </div>
                  <span style={{ flex: 1, fontSize: 13 }}><strong>{c.name}</strong> — {c.deliverable}</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--teal)' }}>{formatCurrency(c.rate)} × {numDays} = {formatCurrency(c.rate * numDays)}</span>
                  <button className="btn btn-ghost btn-sm btn-icon" onClick={() => setContractors(prev => prev.filter(x => x.id !== c.id))}>
                    <Trash2 size={12} color="var(--red)" />
                  </button>
                </div>
              ))}
              <div style={{ padding: '8px 10px', display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: 13 }}>
                <span>Contractor Total ({numDays} day{numDays > 1 ? 's' : ''})</span>
                <span style={{ color: 'var(--teal)' }}>{formatCurrency(contractorTotal * numDays)}</span>
              </div>
            </div>
          )}

          {/* Add contractor form */}
          {showCForm ? (
            <div className="card" style={{ border: '1.5px solid var(--border-light)', marginBottom: 14 }}>
              <h4 style={{ marginBottom: 14 }}>Add Contractor</h4>
              <div className="form-group">
                <label>Contractor</label>
                <select className="select" value={draftCName} onChange={e => handleContractorChange(e.target.value)}>
                  <option value="">Select contractor</option>
                  {CONTRACTOR_NAMES.map(n => <option key={n}>{n}</option>)}
                  <option value="__other__">Other / type your own…</option>
                </select>
              </div>

              {draftCName === '__other__' && (
                <div>
                  <div className="form-group">
                    <label>Contractor Name</label>
                    <input className="input" placeholder="Type contractor name" value={customCName} onChange={e => setCustomCName(e.target.value)} />
                  </div>
                  <label>Deliverables & Agreed Rate (₹/day)</label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 6 }}>
                    {customCRows.map((row, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <input className="input" placeholder="Deliverable name" value={row.deliverable}
                          onChange={e => { const updated = [...customCRows]; updated[i] = { ...updated[i], deliverable: e.target.value }; setCustomCRows(updated); }}
                          style={{ flex: 1 }} />
                        <input type="number" min={0} placeholder="Enter ₹/day" value={row.rate}
                          onChange={e => { const updated = [...customCRows]; updated[i] = { ...updated[i], rate: e.target.value === '' ? '' : Number(e.target.value) }; setCustomCRows(updated); }}
                          style={{ width: 130, padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', background: '#fff' }} />
                        {customCRows.length > 1 && (
                          <button className="btn btn-ghost btn-sm btn-icon" onClick={() => setCustomCRows(customCRows.filter((_, ri) => ri !== i))}>
                            <Trash2 size={13} color="var(--red-light)" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  <button className="btn btn-ghost btn-sm" style={{ marginTop: 8 }} onClick={() => setCustomCRows([...customCRows, { deliverable: '', rate: '' }])}>
                    <Plus size={13} /> Add another deliverable
                  </button>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>
                    No DB rate reference for manual entries — double-check the agreed rate before saving.
                  </div>
                </div>
              )}

              {draftCName && draftCName !== '__other__' && draftCSelected.length > 0 && (
                <div>
                  <label>Select Deliverables & Enter Agreed Rate (₹/day)</label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 6 }}>
                    {draftCSelected.map((item, i) => {
                      const dbEntry = getContractorDeliverables(draftCName).find(d => d.deliverable === item.deliverable);
                      const hasRate = item.rate !== '' && Number(item.rate) > 0;
                      return (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 'var(--radius-sm)', background: hasRate ? 'var(--teal-bg)' : '#F7FAFC', border: `1px solid ${hasRate ? 'var(--teal)' : 'var(--border)'}` }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 5 }}>{item.deliverable}</div>
                            {dbEntry && dbEntry.rateOptions.length > 0 ? (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                {dbEntry.rateOptions.map(opt => {
                                  const isPicked = Number(item.rate) === opt.rate;
                                  return (
                                    <button key={opt.label} type="button"
                                      onClick={() => {
                                        const updated = [...draftCSelected];
                                        updated[i] = { ...updated[i], rate: opt.rate };
                                        setDraftCSelected(updated);
                                      }}
                                      style={{ fontSize: 11, padding: '3px 9px', borderRadius: 12, border: `1px solid ${isPicked ? 'var(--teal)' : 'var(--border)'}`, background: isPicked ? 'var(--teal)' : '#fff', color: isPicked ? '#fff' : 'var(--text-secondary)', cursor: 'pointer', fontFamily: 'inherit', fontWeight: isPicked ? 600 : 400 }}>
                                      {opt.label}: ₹{opt.rate.toLocaleString('en-IN')}
                                    </button>
                                  );
                                })}
                              </div>
                            ) : (
                              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>No DB rate on file — enter manually</div>
                            )}
                          </div>
                          <input type="number" min={0} placeholder="Or custom ₹/day"
                            value={item.rate}
                            onChange={e => {
                              const updated = [...draftCSelected];
                              updated[i] = { ...updated[i], rate: e.target.value === '' ? '' : Number(e.target.value) };
                              setDraftCSelected(updated);
                            }}
                            style={{ width: 130, padding: '6px 10px', border: `1px solid ${hasRate ? 'var(--teal)' : 'var(--border)'}`, borderRadius: 6, fontSize: 13, fontFamily: 'inherit', background: '#fff', flexShrink: 0 }} />
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>
                    Leave blank to skip a deliverable. Only filled ones will be added.
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                <button className="btn btn-secondary btn-sm" onClick={() => { setShowCForm(false); setDraftCName(''); setDraftCSelected([]); setCustomCName(''); setCustomCRows([{ deliverable: '', rate: '' }]); }}>Cancel</button>
                <button className="btn btn-teal btn-sm" onClick={addContractors}
                  disabled={draftCName === '__other__'
                    ? !customCName.trim() || !customCRows.some(r => r.deliverable.trim() && r.rate !== '' && Number(r.rate) > 0)
                    : !draftCName || !draftCSelected.some(d => d.rate !== '' && Number(d.rate) > 0)}>
                  <Check size={13} /> Add Selected
                </button>
              </div>
            </div>
          ) : (
            <button onClick={() => setShowCForm(true)}
              style={{ width: '100%', padding: '10px', borderRadius: 'var(--radius-md)', border: '1.5px dashed var(--border-light)', background: '#fff', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontFamily: 'inherit', marginBottom: 14 }}>
              <Plus size={14} /> Add Contractor
            </button>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <button className="btn btn-secondary" onClick={() => setStep(1)}><ChevronLeft size={14} /> Back</button>
            <button className="btn btn-primary" onClick={() => setStep(3)}>Continue <ChevronRight size={14} /></button>
          </div>
        </div>
      )}

      {/* ── STEP 3: PP Vendors ── */}
      {step === 3 && (
        <div>
          <h3 className="gold-accent" style={{ marginBottom: 4 }}>Post Production</h3>
          <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 16 }}>
            Optional — pick PP vendor → deliverables load from DB → enter agreed rates. You can also skip this entirely.
          </p>

          <div style={{ display:'flex', gap:10, marginBottom:16 }}>
            <button onClick={() => { setIncludePP(false); setPPItems([]); }}
              style={{ flex:1, padding:'10px', borderRadius:'var(--radius-sm)', border:`1.5px solid ${!includePP?'var(--teal)':'var(--border)'}`, background:!includePP?'var(--teal-bg)':'#fff', cursor:'pointer', fontFamily:'inherit', fontSize:13, fontWeight:!includePP?600:400, color:!includePP?'var(--teal)':'var(--text-secondary)' }}>
              No Post Production
            </button>
            <button onClick={() => setIncludePP(true)}
              style={{ flex:1, padding:'10px', borderRadius:'var(--radius-sm)', border:`1.5px solid ${includePP?'var(--blue)':'var(--border)'}`, background:includePP?'var(--blue-bg)':'#fff', cursor:'pointer', fontFamily:'inherit', fontSize:13, fontWeight:includePP?600:400, color:includePP?'var(--blue)':'var(--text-secondary)' }}>
              Include Post Production
            </button>
          </div>
          {!includePP && (
            <div style={{ display:'flex', justifyContent:'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setStep(2)} style={{ marginRight:8 }}><ChevronLeft size={14}/> Back</button>
              <button className="btn btn-primary" onClick={() => setStep(4)}>Continue <ChevronRight size={14}/></button>
            </div>
          )}
          {includePP && (
          <div>

          {ppItems.length > 0 && (
            <div className="card" style={{ marginBottom: 14 }}>
              {ppItems.map((v, i) => (
                <div key={v.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderBottom: i < ppItems.length - 1 ? '1px solid var(--border)' : 'none' }}>
                  <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--blue-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: 'var(--blue)', flexShrink: 0 }}>
                    {v.name[0]}
                  </div>
                  <span style={{ flex: 1, fontSize: 13 }}><strong>{v.name}</strong> — {v.deliverable}{v.qty > 1 ? ` × ${v.qty}` : ''}</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--blue)' }}>{formatCurrency(v.rate * (v.qty||1))}</span>
                  <button className="btn btn-ghost btn-sm btn-icon" onClick={() => setPPItems(prev => prev.filter(x => x.id !== v.id))}>
                    <Trash2 size={12} color="var(--red)" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {showPPForm ? (
            <div className="card" style={{ border: '1.5px solid var(--border-light)', marginBottom: 14 }}>
              <h4 style={{ marginBottom: 14 }}>Add PP Deliverable</h4>
              <div className="form-group">
                <label>PP Vendor</label>
                <select className="select" value={draftPPName} onChange={e => handlePPChange(e.target.value)}>
                  <option value="">Select vendor</option>
                  {PP_VENDOR_NAMES.map(n => <option key={n}>{n}</option>)}
                  <option value="__other__">Other / type your own…</option>
                </select>
              </div>

              {draftPPName === '__other__' && (
                <div>
                  <div className="form-group">
                    <label>PP Vendor Name</label>
                    <input className="input" placeholder="Type vendor name" value={customPPName} onChange={e => setCustomPPName(e.target.value)} />
                  </div>
                  <label>Deliverables & Agreed Rate (₹)</label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 6 }}>
                    {customPPRows.map((row, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <input className="input" placeholder="Deliverable name" value={row.deliverable}
                          onChange={e => { const updated = [...customPPRows]; updated[i] = { ...updated[i], deliverable: e.target.value }; setCustomPPRows(updated); }}
                          style={{ flex: 1 }} />
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                          <input type="number" min={1} placeholder="Qty" value={row.qty || 1}
                            onChange={e => { const updated = [...customPPRows]; updated[i] = { ...updated[i], qty: Number(e.target.value) || 1 }; setCustomPPRows(updated); }}
                            style={{ width: 50, padding: '6px 6px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', background: '#fff', textAlign: 'center' }} />
                          <input type="number" min={0} placeholder="Enter ₹" value={row.rate}
                            onChange={e => { const updated = [...customPPRows]; updated[i] = { ...updated[i], rate: e.target.value === '' ? '' : Number(e.target.value) }; setCustomPPRows(updated); }}
                            style={{ width: 110, padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', background: '#fff' }} />
                        </div>
                        {customPPRows.length > 1 && (
                          <button className="btn btn-ghost btn-sm btn-icon" onClick={() => setCustomPPRows(customPPRows.filter((_, ri) => ri !== i))}>
                            <Trash2 size={13} color="var(--red-light)" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  <button className="btn btn-ghost btn-sm" style={{ marginTop: 8 }} onClick={() => setCustomPPRows([...customPPRows, { deliverable: '', rate: '', qty: 1 }])}>
                    <Plus size={13} /> Add another deliverable
                  </button>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>
                    No DB rate reference for manual entries — double-check the agreed rate before saving.
                  </div>
                </div>
              )}

              {draftPPName && draftPPName !== '__other__' && draftPPSelected.length > 0 && (
                <div>
                  <label>Select Deliverables & Enter Agreed Rate (₹)</label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 6 }}>
                    {draftPPSelected.map((item, i) => {
                      const dbEntry = getPPDeliverables(draftPPName).find(d => d.deliverable === item.deliverable);
                      const hasRate = item.rate !== '' && Number(item.rate) > 0;
                      return (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 'var(--radius-sm)', background: hasRate ? 'var(--blue-bg)' : '#F7FAFC', border: `1px solid ${hasRate ? 'var(--blue)' : 'var(--border)'}` }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 5 }}>{item.deliverable}</div>
                            {dbEntry && dbEntry.rateOptions.length > 0 ? (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                {dbEntry.rateOptions.map(opt => {
                                  const isPicked = Number(item.rate) === opt.rate;
                                  return (
                                    <button key={opt.label} type="button"
                                      onClick={() => {
                                        const updated = [...draftPPSelected];
                                        updated[i] = { ...updated[i], rate: opt.rate };
                                        setDraftPPSelected(updated);
                                      }}
                                      style={{ fontSize: 11, padding: '3px 9px', borderRadius: 12, border: `1px solid ${isPicked ? 'var(--blue)' : 'var(--border)'}`, background: isPicked ? 'var(--blue)' : '#fff', color: isPicked ? '#fff' : 'var(--text-secondary)', cursor: 'pointer', fontFamily: 'inherit', fontWeight: isPicked ? 600 : 400 }}>
                                      {opt.label}: ₹{opt.rate.toLocaleString('en-IN')}
                                    </button>
                                  );
                                })}
                              </div>
                            ) : (
                              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>No DB rate on file — enter manually</div>
                            )}
                          </div>
                          <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                            <input type="number" min={1} placeholder="Qty" value={item.qty || 1}
                              onChange={e => {
                                const updated = [...draftPPSelected];
                                updated[i] = { ...updated[i], qty: Number(e.target.value) || 1 };
                                setDraftPPSelected(updated);
                              }}
                              style={{ width: 50, padding: '6px 6px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', background: '#fff', textAlign:'center' }} />
                            <input type="number" min={0} placeholder="Or custom ₹"
                              value={item.rate}
                              onChange={e => {
                                const updated = [...draftPPSelected];
                                updated[i] = { ...updated[i], rate: e.target.value === '' ? '' : Number(e.target.value) };
                                setDraftPPSelected(updated);
                              }}
                              style={{ width: 110, padding: '6px 10px', border: `1px solid ${hasRate ? 'var(--blue)' : 'var(--border)'}`, borderRadius: 6, fontSize: 13, fontFamily: 'inherit', background: '#fff' }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>Leave blank to skip.</div>
                </div>
              )}

              <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                <button className="btn btn-secondary btn-sm" onClick={() => { setShowPPForm(false); setDraftPPName(''); setDraftPPSelected([]); setCustomPPName(''); setCustomPPRows([{ deliverable: '', rate: '', qty: 1 }]); }}>Cancel</button>
                <button style={{ background: 'var(--blue)', color: '#fff', padding: '5px 12px', borderRadius: 'var(--radius-sm)', border: 'none', cursor: 'pointer', fontSize: 13, fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 6 }}
                  onClick={addPPItems} disabled={draftPPName === '__other__'
                    ? !customPPName.trim() || !customPPRows.some(r => r.deliverable.trim() && r.rate !== '' && Number(r.rate) > 0)
                    : !draftPPName || !draftPPSelected.some(d => d.rate !== '' && Number(d.rate) > 0)}>
                  <Check size={13} /> Add Selected
                </button>
              </div>
            </div>
          ) : (
            <button onClick={() => setShowPPForm(true)}
              style={{ width: '100%', padding: '10px', borderRadius: 'var(--radius-md)', border: '1.5px dashed var(--border-light)', background: '#fff', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontFamily: 'inherit', marginBottom: 14 }}>
              <Plus size={14} /> Add PP Deliverable
            </button>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <button className="btn btn-secondary" onClick={() => setStep(2)}><ChevronLeft size={14} /> Back</button>
            <button className="btn btn-primary" onClick={() => setStep(4)}>Continue <ChevronRight size={14} /></button>
          </div>
          </div>
          )}
        </div>
      )}

      {/* ── STEP 4: Album ── */}
      {step === 4 && (
        <div>
          <h3 className="gold-accent" style={{ marginBottom: 6 }}>Album</h3>
          <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 16 }}>Optional — include album in this quote.</p>

          <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
            <button onClick={() => setIncludeAlbum(false)}
              style={{ flex: 1, padding: '10px', borderRadius: 'var(--radius-sm)', border: `1.5px solid ${!includeAlbum ? 'var(--teal)' : 'var(--border)'}`, background: !includeAlbum ? 'var(--teal-bg)' : '#fff', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: !includeAlbum ? 600 : 400, color: !includeAlbum ? 'var(--teal)' : 'var(--text-secondary)' }}>
              No Album
            </button>
            <button onClick={() => setIncludeAlbum(true)}
              style={{ flex: 1, padding: '10px', borderRadius: 'var(--radius-sm)', border: `1.5px solid ${includeAlbum ? 'var(--gold)' : 'var(--border)'}`, background: includeAlbum ? 'var(--gold-bg)' : '#fff', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: includeAlbum ? 600 : 400, color: includeAlbum ? 'var(--gold)' : 'var(--text-secondary)' }}>
              Include Album
            </button>
          </div>

          {includeAlbum && (
            <div className="card" style={{ marginBottom: 14 }}>
              <div className="form-row" style={{ marginBottom: 12 }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Vendor</label>
                  <select className="select" value={vendorName} onChange={e => { setVendorName(e.target.value); setPaperCode(''); setCoverCode(''); }}>
                    <option value="">Select vendor</option>
                    {vendorList.map(v => <option key={v}>{v}</option>)}
                  </select>
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Paper Type</label>
                  <select className="select" value={paperCode} onChange={e => setPaperCode(e.target.value)} disabled={!vendorName}>
                    <option value="">Select paper</option>
                    {sizes.map(size => (
                      <optgroup key={size} label={`Size ${size}`}>
                        {papers.filter(p => p.size === size).map(p => (
                          <option key={p.paperCode} value={p.paperCode}>{p.paperType} — ₹{p.ratePerSheet}/sheet</option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-row" style={{ marginBottom: 12 }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Cover Type</label>
                  <select className="select" value={coverCode} onChange={e => setCoverCode(e.target.value)} disabled={!vendorName}>
                    <option value="">Select cover</option>
                    {covers.map(c => <option key={c.coverCode} value={c.coverCode}>{c.coverType} — ₹{c.rate}</option>)}
                  </select>
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Sheets</label>
                  <input type="number" className="input" min={1} value={sheets} onChange={e => setSheets(Number(e.target.value))} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>No. of Albums</label>
                  <input type="number" className="input" min={1} value={numAlbums} onChange={e => setNumAlbums(Number(e.target.value))} />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Design Tier</label>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {(['Standard', 'Premium', 'Elite'] as const).map(t => (
                      <button key={t} onClick={() => setDesignTier(t)}
                        style={{ flex: 1, padding: '8px 4px', borderRadius: 'var(--radius-sm)', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, fontWeight: designTier === t ? 600 : 400, background: designTier === t ? 'var(--gold-bg)' : '#fff', border: `1.5px solid ${designTier === t ? 'var(--gold)' : 'var(--border)'}`, color: designTier === t ? 'var(--gold)' : 'var(--text-secondary)' }}>
                        {t}<div style={{ fontSize: 10 }}>₹{DESIGN_CHARGES[t]}</div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              {selPaper && selCover && (
                <div style={{ marginTop: 12, padding: '8px 12px', background: 'var(--gold-bg)', border: '1px solid var(--gold)', borderRadius: 6, fontSize: 13, display: 'flex', justifyContent: 'space-between' }}>
                  <span>{sheets} sheets × ₹{selPaper.ratePerSheet} + cover ₹{selCover.rate} + design ₹{designCharge} × {numAlbums} album(s)</span>
                  <strong style={{ color: 'var(--gold)' }}>{formatCurrency(albumCost)}</strong>
                </div>
              )}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <button className="btn btn-secondary" onClick={() => setStep(3)}><ChevronLeft size={14} /> Back</button>
            <button className="btn btn-primary" onClick={() => setStep(5)}>Continue <ChevronRight size={14} /></button>
          </div>
        </div>
      )}

      {/* ── STEP 5: Summary ── */}
      {step === 5 && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 20 }}>
          <div>
            <h3 className="gold-accent" style={{ marginBottom: 16 }}>Quote Summary</h3>

            {totalCost === 0 && (
              <div className="alert alert-amber" style={{ marginBottom:14 }}>
                ⚠️ No contractor, PP, or album cost added yet. Go back and add at least one item, or the quote will be ₹0.
              </div>
            )}

            <div className="card" style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Customer</div>
              <div style={{ fontWeight: 700, fontSize: 16 }}>{customerName}</div>
              {customerPhone && <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{customerPhone}</div>}
              <div style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: 4 }}>{eventName}</div>
              {eventDates.filter(d => d.date).map((ed, i) => (
                <div key={i} style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                  📅 {new Date(ed.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })} — {ed.label}
                </div>
              ))}
              {notes && <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 4 }}>{notes}</div>}
            </div>

            {contractors.length > 0 && (
              <div className="card" style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>Contractors × {numDays} day(s)</div>
                {contractors.map((c, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6 }}>
                    <span>{c.name} — {c.deliverable}</span>
                    <span style={{ fontWeight: 500 }}>₹{c.rate.toLocaleString('en-IN')}/day × {numDays} = {formatCurrency(c.rate * numDays)}</span>
                  </div>
                ))}
                <div className="divider" style={{ margin: '8px 0' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, fontWeight: 700 }}>
                  <span>Contractor Total</span>
                  <span style={{ color: 'var(--teal)' }}>{formatCurrency(contractorTotal * numDays)}</span>
                </div>
              </div>
            )}

            {ppItems.length > 0 && (
              <div className="card" style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>Post Production</div>
                {ppItems.map((v, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6 }}>
                    <span>{v.name} — {v.deliverable}{v.qty > 1 ? ` × ${v.qty}` : ''}</span>
                    <span style={{ fontWeight: 500 }}>{formatCurrency(v.rate * (v.qty||1))}</span>
                  </div>
                ))}
                <div className="divider" style={{ margin: '8px 0' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, fontWeight: 700 }}>
                  <span>PP Total</span>
                  <span style={{ color: 'var(--blue)' }}>{formatCurrency(ppTotal)}</span>
                </div>
              </div>
            )}

            {includeAlbum && selPaper && selCover && (
              <div className="card" style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Album</div>
                <div style={{ fontSize: 13 }}>{vendorName} · {selPaper.paperType} · {selCover.coverType}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{sheets} sheets × {numAlbums} album(s) · {designTier} design</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontWeight: 700, fontSize: 13 }}>
                  <span>Album Total</span>
                  <span style={{ color: 'var(--gold)' }}>{formatCurrency(albumCost)}</span>
                </div>
              </div>
            )}
          </div>

          <div>
            <div className="card" style={{ marginBottom: 12 }}>
              <h4 style={{ marginBottom: 12 }}>Markup</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {([35, 45, 55] as MarkupType[]).map(pct => (
                  <button key={pct} onClick={() => setMarkupType(pct)}
                    style={{ padding: '10px 14px', borderRadius: 'var(--radius-sm)', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', background: markupType === pct ? 'var(--gold)' : '#fff', color: markupType === pct ? '#fff' : 'var(--text-secondary)', border: `1.5px solid ${markupType === pct ? 'var(--gold)' : 'var(--border)'}`, textAlign: 'left' }}>
                    {pct}%
                  </button>
                ))}
              </div>
            </div>

            <div className="card" style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 13 }}>
                <span style={{ color: 'var(--text-secondary)' }}>Contractors</span>
                <span>{formatCurrency(contractorTotal * numDays)}</span>
              </div>
              {ppTotal > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 13 }}>
                  <span style={{ color: 'var(--text-secondary)' }}>Post Production</span>
                  <span>{formatCurrency(ppTotal)}</span>
                </div>
              )}
              {albumCost > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 13 }}>
                  <span style={{ color: 'var(--text-secondary)' }}>Album</span>
                  <span>{formatCurrency(albumCost)}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 13 }}>
                <span style={{ color: 'var(--text-secondary)' }}>Markup ({markupPercent}%)</span>
                <span style={{ color: 'var(--amber)' }}>{formatCurrency(markupAmount)}</span>
              </div>
              <div className="divider" />
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontWeight: 700, fontSize: 16 }}>Quoted Amount</span>
                <span style={{ fontWeight: 700, fontSize: 22, color: 'var(--gold)' }}>{formatCurrency(quotedAmount)}</span>
              </div>
            </div>

            <button className="btn btn-primary btn-lg" style={{ width: '100%', justifyContent: 'center', marginBottom: 8 }} onClick={handleSave}>
              <Save size={15} /> Save Quote
            </button>
            <button className="btn btn-secondary" style={{ width: '100%', justifyContent: 'center', marginBottom: 12 }} onClick={() => window.print()}>
              🖨 Print
            </button>
            <button className="btn btn-secondary" onClick={() => setStep(4)}><ChevronLeft size={14} /> Back</button>
          </div>
        </div>
      )}
    </div>
  );
}
